import React from "react";
import { pricingHeroParts, pricingPillParts } from "../lib/format";

const CURRENCY_MUTED = "color-mix(in srgb, var(--ink) 55%, transparent)";

/** Borderless ticket price — amount large, currency small. */
function CardPriceAmount({ amount, currency }) {
  return (
    <span className="inline-flex items-baseline">
      <span className="font-ui text-xl font-semibold text-ink tabular-nums leading-none">
        {amount}
      </span>
      <span
        className="font-ui text-sm font-normal leading-none ml-0.5"
        style={{ color: CURRENCY_MUTED }}
      >
        {currency}
      </span>
    </span>
  );
}

function CardFreeLabel() {
  return (
    <span className="font-ui text-[15px] font-medium text-ink/55 leading-none uppercase tracking-wide">
      Free
    </span>
  );
}

function HeroPriceAmount({ amount, currency, size = "md" }) {
  const sizes = {
    sm: { amount: "text-[18px]", currency: "text-[11px]" },
    md: { amount: "text-[22px]", currency: "text-[12px]" },
  };
  const s = sizes[size] || sizes.md;
  return (
    <span className="inline-flex items-baseline gap-1 text-ink">
      <span className={`${s.amount} leading-none font-bold tabular-nums`}>{amount}</span>
      <span className={`${s.currency} leading-none font-semibold text-ink/75`}>{currency}</span>
    </span>
  );
}

function HeroFreeLabel() {
  return (
    <span className="font-ui text-[13px] font-medium text-white/75 leading-none uppercase tracking-wide">
      Free
    </span>
  );
}

/** Event card pricing — borderless ticket style in the card corner. */
export function PricePill({ pricing, rsvpCount = 0, className = "", ...rest }) {
  const parts = pricingPillParts(pricing, { rsvpCount });
  if (parts.type === "free") {
    return (
      <span className={`shrink-0 ${className}`} {...rest}>
        <CardFreeLabel />
      </span>
    );
  }
  return (
    <span
      className={`shrink-0 inline-flex items-baseline gap-1 ${className}`}
      {...rest}
    >
      {parts.prefix && (
        <span
          className="font-ui text-[11px] font-medium uppercase tracking-wide mr-0.5"
          style={{ color: CURRENCY_MUTED }}
        >
          {parts.prefix}
        </span>
      )}
      <CardPriceAmount amount={parts.amount} currency={parts.currency} />
    </span>
  );
}

/** Event hero pricing pill — larger amount, smaller currency. */
export function PriceHeroPill({ pricing, rsvpCount = 0, className = "", ...rest }) {
  const parts = pricingHeroParts(pricing, { rsvpCount });
  if (parts.type === "free") {
    return (
      <span className={`shrink-0 ${className}`} {...rest}>
        <HeroFreeLabel />
      </span>
    );
  }
  if (parts.type === "early-combo") {
    return (
      <div
        className={`shrink-0 rounded-btn bg-white/95 border border-black/[0.06] px-3.5 py-2 font-ui inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 max-w-[min(100%,14rem)] justify-end ${className}`}
        {...rest}
      >
        <span className="text-[11px] font-medium uppercase tracking-wide text-ink/65">{parts.prefix}</span>
        <HeroPriceAmount amount={parts.earlyAmount} currency={parts.currency} size="md" />
        <span className="text-[11px] font-medium text-ink/55">· then</span>
        <HeroPriceAmount amount={parts.regularAmount} currency={parts.currency} size="md" />
      </div>
    );
  }
  if (parts.type === "self-managed") {
    return (
      <div
        className={`shrink-0 rounded-btn bg-white/95 border border-black/[0.06] px-3.5 py-2 font-ui inline-flex items-center gap-1.5 ${className}`}
        {...rest}
      >
        <span className="text-[11px] font-medium uppercase tracking-wide text-ink/65">{parts.prefix}</span>
        <HeroPriceAmount amount={parts.amount} currency={parts.currency} size="md" />
        <span className="text-[11px] font-medium text-ink/55">{parts.suffix}</span>
      </div>
    );
  }
  return (
    <div
      className={`shrink-0 rounded-btn bg-white/95 border border-black/[0.06] px-3.5 py-2 font-ui ${className}`}
      {...rest}
    >
      <HeroPriceAmount amount={parts.amount} currency={parts.currency} size="md" />
    </div>
  );
}
